// =============================================================================
// API Client
// =============================================================================

import { API_CONFIG, buildApiUrl, getAuthHeaders, DEFAULT_REQUEST_CONFIG } from '../config/api.js';

class APIClient {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.defaultHeaders = {
      'Content-Type': 'application/json'
    };

    // 调试信息
    if (import.meta.env.DEV) {
      console.log('🔧 APIClient initialized with:', {
        baseURL: this.baseURL,
        timeout: this.timeout,
        mode: API_CONFIG.MODE,
      });
    }
  }

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem('auth_token');
  }

  // Build headers with auth token
  buildHeaders(customHeaders = {}) {
    const headers = { ...this.defaultHeaders, ...customHeaders };

    // 使用统一的认证头获取方法
    const authHeaders = getAuthHeaders();
    Object.assign(headers, authHeaders);

    return headers;
  }

  // Build full URL
  buildURL(endpoint, params = {}) {
    if (endpoint.startsWith('http')) {
      return endpoint;
    }

    // 使用统一的 URL 构建方法
    if (Object.keys(params).length > 0) {
      return buildApiUrl(endpoint, params);
    }

    // 简单的 URL 拼接
    const baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    return `${baseURL}${cleanEndpoint}`;
  }

  // Handle response
  async handleResponse(response) {
    const contentType = response.headers.get('content-type');
    
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      const error = new Error(data.message || data.error || `HTTP ${response.status}`);
      error.status = response.status;
      error.response = { data, status: response.status };
      throw error;
    }

    return { data, status: response.status };
  }

  // Generic request method
  async request(method, endpoint, options = {}) {
    const {
      data,
      headers: customHeaders = {},
      timeout = this.timeout,
      ...otherOptions
    } = options;

    const url = this.buildURL(endpoint);
    const headers = this.buildHeaders(customHeaders);

    const config = {
      method: method.toUpperCase(),
      headers,
      ...otherOptions
    };

    // Add body for non-GET requests
    if (data && method.toUpperCase() !== 'GET') {
      if (data instanceof FormData) {
        // Remove Content-Type header for FormData (browser will set it with boundary)
        delete config.headers['Content-Type'];
        config.body = data;
      } else {
        config.body = JSON.stringify(data);
      }
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    config.signal = controller.signal;

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      return await this.handleResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      
      // Handle network errors
      if (!error.response) {
        throw new Error('Network error - please check your connection');
      }
      
      throw error;
    }
  }

  // GET request
  async get(endpoint, options = {}) {
    const { params, ...otherOptions } = options;
    
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      url += `?${searchParams.toString()}`;
    }
    
    return this.request('GET', url, otherOptions);
  }

  // POST request
  async post(endpoint, data, options = {}) {
    return this.request('POST', endpoint, { data, ...options });
  }

  // PUT request
  async put(endpoint, data, options = {}) {
    return this.request('PUT', endpoint, { data, ...options });
  }

  // PATCH request
  async patch(endpoint, data, options = {}) {
    return this.request('PATCH', endpoint, { data, ...options });
  }

  // DELETE request
  async delete(endpoint, options = {}) {
    return this.request('DELETE', endpoint, options);
  }

  // Upload file
  async upload(endpoint, file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional fields if provided
    if (options.fields) {
      Object.entries(options.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    
    return this.request('POST', endpoint, {
      data: formData,
      ...options
    });
  }

  // Download file
  async download(endpoint, options = {}) {
    const response = await this.request('GET', endpoint, {
      ...options,
      headers: {
        ...options.headers,
        'Accept': 'application/octet-stream'
      }
    });
    
    return response.data;
  }

  // Stream data (for real-time updates)
  async stream(endpoint, onData, options = {}) {
    const url = this.buildURL(endpoint);
    const headers = this.buildHeaders(options.headers);

    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: options.signal
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        onData(chunk);
      }
    } finally {
      reader.releaseLock();
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.get('/health', { timeout: 5000 });
      return response.data;
    } catch (error) {
      return { status: 'unhealthy', error: error.message };
    }
  }

  // Retry mechanism
  async retry(requestFn, maxAttempts = 3, delay = 1000) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on client errors (4xx)
        if (error.status >= 400 && error.status < 500) {
          throw error;
        }
        
        // Don't retry on last attempt
        if (attempt === maxAttempts) {
          throw error;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
    
    throw lastError;
  }

  // Batch requests
  async batch(requests) {
    const promises = requests.map(({ method, endpoint, data, options }) => 
      this.request(method, endpoint, { data, ...options }).catch(error => ({ error }))
    );
    
    return Promise.all(promises);
  }
}

// Create and export singleton instance
export const apiClient = new APIClient();
